import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Plus, Link2, Copy, Check, Ban } from 'lucide-react';
import {
  trialInvitesApi,
  trialInviteUrl,
  type CreateTrialInvitePayload,
  type TrialInviteLinkWithConversions,
} from '@/api/trialInvites';
import { Button, Modal, Input, Loading, EmptyState } from '@/components/ui';
import { formatDate } from '@/utils/date';

function usesLabel(l: TrialInviteLinkWithConversions): string {
  const max = l.max_uses != null ? String(l.max_uses) : '∞';
  return `${l.use_count} / ${max}`;
}

function expiryLabel(l: TrialInviteLinkWithConversions): string {
  if (!l.expires_at) return 'Never';
  return formatDate(l.expires_at);
}

function CopyLinkButton({ token }: { token: string }) {
  const [copied, setCopied] = useState(false);
  const url = trialInviteUrl(token);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Link copied');
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Could not copy');
    }
  };

  return (
    <div className="flex items-center gap-2 max-w-[320px]">
      <code className="font-mono text-xs text-ink-secondary truncate" title={url}>
        {url}
      </code>
      <button
        type="button"
        onClick={copy}
        className="text-indigo-400 hover:text-indigo-300 flex-shrink-0"
        title="Copy link"
      >
        {copied ? <Check size={15} /> : <Copy size={15} />}
      </button>
    </div>
  );
}

export default function TrialInvitesPage() {
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);

  const { data: links, isLoading } = useQuery({
    queryKey: ['trial-invites'],
    queryFn: () => trialInvitesApi.list(),
  });

  const deactivateMut = useMutation({
    mutationFn: (id: string) => trialInvitesApi.deactivate(id),
    onSuccess: () => {
      toast.success('Invite link deactivated');
      qc.invalidateQueries({ queryKey: ['trial-invites'] });
    },
    onError: (e: Error) => toast.error(e.message ?? 'Failed'),
  });

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Link2 className="h-5 w-5 text-indigo-400" />
          <div>
            <h1 className="text-xl font-semibold text-white">Trial Invite Links</h1>
            <p className="text-xs text-ink-muted mt-0.5">
              Shareable self-serve trial links — set the trial length per link and track conversions
            </p>
          </div>
        </div>
        <Button onClick={() => setModalOpen(true)}>
          <Plus size={16} className="mr-1" /> New invite link
        </Button>
      </div>

      {isLoading ? (
        <Loading text="Loading invite links…" />
      ) : !(links ?? []).length ? (
        <EmptyState
          icon={<Link2 className="h-8 w-8" />}
          title="No invite links yet"
          description="Create a named trial link to share with a coach or campaign"
        />
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-ink-muted text-xs uppercase">
                <th className="px-4 py-3">Label</th>
                <th className="px-4 py-3">Link</th>
                <th className="px-4 py-3">Trial</th>
                <th className="px-4 py-3">Uses</th>
                <th className="px-4 py-3">Signed up</th>
                <th className="px-4 py-3">Expires</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {links!.map((l) => (
                <tr key={l.id} className="border-t border-navy-border hover:bg-white/[0.02] align-top">
                  <td className="px-4 py-3 text-white font-medium">
                    {l.label}
                    {(l.invited_email || l.invited_phone) && (
                      <div className="text-xs text-ink-muted mt-0.5">
                        {l.invited_email ?? l.invited_phone}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <CopyLinkButton token={l.token} />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">{l.trial_days} days</td>
                  <td className="px-4 py-3 whitespace-nowrap">{usesLabel(l)}</td>
                  <td className="px-4 py-3">
                    {l.conversions.length === 0 ? (
                      <span className="text-ink-muted">—</span>
                    ) : (
                      <div className="space-y-0.5">
                        {l.conversions.map((c) => (
                          <div key={c.id} className="text-xs">
                            <span className="text-white">{c.name}</span>{' '}
                            <span className="text-ink-muted">· {formatDate(c.created_at)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">{expiryLabel(l)}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`text-xs font-medium px-2 py-1 rounded-full ${
                        l.is_active ? 'bg-teal-500/20 text-teal-300' : 'bg-gray-500/20 text-gray-400'
                      }`}
                    >
                      {l.is_active ? 'active' : 'inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">{formatDate(l.created_at)}</td>
                  <td className="px-4 py-3">
                    {l.is_active && (
                      <button
                        type="button"
                        onClick={() => deactivateMut.mutate(l.id)}
                        disabled={deactivateMut.isPending}
                        className="text-red-400 hover:text-red-300"
                        title="Deactivate"
                      >
                        <Ban size={16} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateInviteModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={() => {
          setModalOpen(false);
          qc.invalidateQueries({ queryKey: ['trial-invites'] });
        }}
      />
    </div>
  );
}

function CreateInviteModal({
  open,
  onClose,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [label, setLabel] = useState('');
  const [invitedEmail, setInvitedEmail] = useState('');
  const [invitedPhone, setInvitedPhone] = useState('');
  const [trialDays, setTrialDays] = useState('60');
  const [maxUses, setMaxUses] = useState('1');
  const [expiresAt, setExpiresAt] = useState('');

  useEffect(() => {
    if (open) {
      setLabel('');
      setInvitedEmail('');
      setInvitedPhone('');
      setTrialDays('60');
      setMaxUses('1');
      setExpiresAt('');
    }
  }, [open]);

  const saveMut = useMutation({
    mutationFn: async () => {
      const days = Number.parseInt(trialDays, 10);
      if (!label.trim()) throw new Error('Label is required');
      if (!Number.isFinite(days) || days < 1 || days > 365) {
        throw new Error('Trial days must be between 1 and 365');
      }
      const payload: CreateTrialInvitePayload = {
        label,
        invited_email: invitedEmail.trim() || null,
        invited_phone: invitedPhone.trim() || null,
        trial_days: days,
        max_uses: maxUses.trim() ? Number.parseInt(maxUses, 10) : null,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
      };
      return trialInvitesApi.create(payload);
    },
    onSuccess: () => {
      toast.success('Invite link created');
      onSaved();
    },
    onError: (e: Error) => toast.error(e.message ?? 'Failed'),
  });

  return (
    <Modal isOpen={open} onClose={onClose} title="New trial invite link">
      <div className="space-y-4">
        <div>
          <label className="block text-xs text-ink-muted mb-1">Label (coach name / campaign)</label>
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Dana Levi — coaches Q3"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-ink-muted mb-1">Invited email (optional)</label>
            <Input
              value={invitedEmail}
              onChange={(e) => setInvitedEmail(e.target.value)}
              type="email"
              placeholder="coach@example.com"
            />
          </div>
          <div>
            <label className="block text-xs text-ink-muted mb-1">Invited phone (optional)</label>
            <Input
              value={invitedPhone}
              onChange={(e) => setInvitedPhone(e.target.value)}
              placeholder="972501234567"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-ink-muted mb-1">Trial length (days)</label>
            <Input value={trialDays} onChange={(e) => setTrialDays(e.target.value)} type="number" min={1} max={365} />
            <p className="text-[11px] text-ink-muted mt-1">Invited coaches default to 60 days.</p>
          </div>
          <div>
            <label className="block text-xs text-ink-muted mb-1">Max uses (blank = unlimited)</label>
            <Input value={maxUses} onChange={(e) => setMaxUses(e.target.value)} type="number" min={1} />
          </div>
        </div>

        <div>
          <label className="block text-xs text-ink-muted mb-1">Expires at (optional)</label>
          <Input value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} type="datetime-local" />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
            {saveMut.isPending ? 'Creating…' : 'Create link'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
